import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import FontAwesomeIcon from 'react-fontawesome';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { FormApi, ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { Table, TableCell, TableRow, TableHead, TableBody } from '@mui/material';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { Product, Store } from './types';
import {
    deleteProduct,
    enqueueNotification,
    saveUpdateProduct
} from './actions/Actions';
import { PRODUCTS_NS, required } from './Constants';
import DeleteButton from './DeleteButton';

const requiredArray = (error: string) => (value: any[]) =>
    value && value.length ? undefined : error;

const getDefaultVarietiesNumber = (values: Partial<Product>): number =>
    (values?.productVarieties || []).filter(x => x?.isDefault).length;

const Products: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation();
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const [loading, setLoading] = React.useState(false);
    const [hasVarieties, setHasVarieties] = React.useState(false);
    const [selectedProduct, setSelectedProduct] =
        React.useState<Partial<Product>>(null);

    const products = useSelector(({ app }: Store) => app.products);
    const productCategories = useSelector(({ app }: Store) => app.productCategories);

    // TODO: there is a validation issue with the price and the varieties
    const validateForm = React.useCallback(
        (values: Partial<Product>) => {
            const errors: ValidationErrors = {};
            const numDefaultVarieties = getDefaultVarietiesNumber(values);

            if (numDefaultVarieties === 1 && !_.isNil(values.price)) {
                errors.price = t(`${PRODUCTS_NS}.price_validation`);
            }

            if (numDefaultVarieties > 1) {
                errors.defaultVariety = t(`${PRODUCTS_NS}.variety_validation`);
            }

            return errors;
        },
        [t]
    );

    const handleHasVarietiesChange = React.useCallback(
        (form: FormApi, { id, productVarieties }: Partial<Product>) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const isChecked = e.target.checked;
                setHasVarieties(isChecked);

                if (!_.isNil(id)) {
                    if (!isChecked && productVarieties) {
                        form.mutators.removeBatch(
                            'productVarieties',
                            _.range(0, productVarieties.length)
                        );
                    }

                    if (isChecked && !productVarieties.length && selectedProduct) {
                        form.mutators.concat(
                            'productVarieties',
                            selectedProduct.productVarieties
                        );
                    }
                } else if (productVarieties) {
                    form.mutators.removeBatch(
                        'productVarieties',
                        _.range(0, productVarieties.length)
                    );
                }
            },
        [selectedProduct]
    );

    const setProduct = React.useCallback(
        (product: Product) => () => {
            setSelectedProduct(product);
            setHasVarieties(!_.isEmpty(product.productVarieties));
        },
        []
    );

    const resetStateAndForm = React.useCallback(
        (categoryId: number) => () => {
            setSelectedProduct({ productCategoryID: categoryId });
            setHasVarieties(false);
        },
        []
    );

    const handleProductDeletion = React.useCallback(
        (id: number, form: FormApi) => () => {
            setLoading(true);

            dispatch(deleteProduct(id))
                .then(() => {
                    resetStateAndForm(null)();
                    form.reset({});

                    ['name', 'description', 'productCategoryID', 'price'].forEach(
                        field => {
                            form.resetFieldState(field);
                        }
                    );
                })
                .catch((e: Error) => {
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: e.message,
                            options: { variant: 'error' }
                        })
                    );
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, resetStateAndForm]
    );

    const renderForm = React.useCallback(
        ({
            handleSubmit,
            submitting,
            validating,
            form,
            values,
            errors
        }: FormRenderProps) => {
            const disabled = submitting || validating;
            const {
                mutators: { push }
            } = form;

            return (
                <form className='form' onSubmit={handleSubmit}>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${PRODUCTS_NS}.name`)}:
                        </label>
                        <Field name='name' validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='text' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${PRODUCTS_NS}.description`)}:
                        </label>
                        <div className='field-input'>
                            <Field
                                name='description'
                                component='textarea'
                                className='description'
                            />
                        </div>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${PRODUCTS_NS}.category`)}:
                        </label>
                        <Field
                            name='productCategoryID'
                            validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <select {...input}>
                                        <option />
                                        {productCategories.map(({ id, name }) => (
                                            <option key={id} value={id}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='small-wide'>
                            {t(`${PRODUCTS_NS}.price`)}:
                        </label>
                        <Field
                            name='price'
                            type='number'
                            // eslint-disable-next-line react/jsx-no-bind
                            validate={
                                !getDefaultVarietiesNumber(values)
                                    ? required(t('required_field'))
                                    : undefined
                            }>
                            {({ input, meta }) => (
                                <div className='field-input price'>
                                    <input {...input} min={0} step='any' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                        &euro;
                    </label>
                    <label className='form-field varieties'>
                        <label className='small-wide'>
                            {t(`${PRODUCTS_NS}.varieties`)}:
                        </label>
                        <div className='field-input'>
                            <input
                                type='checkbox'
                                checked={hasVarieties}
                                onChange={handleHasVarietiesChange(form, values)}
                            />
                        </div>
                    </label>
                    {hasVarieties && (
                        <>
                            <FontAwesomeIcon
                                name='plus'
                                className='icon'
                                title={t(`${PRODUCTS_NS}.add_variety`)}
                                // eslint-disable-next-line react/jsx-no-bind
                                onClick={() => push('productVarieties', undefined)}
                            />
                            <FieldArray
                                name='productVarieties'
                                validate={requiredArray(t('required_field'))}>
                                {({ fields }) => (
                                    <Table className='product-varieties'>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell />
                                                <TableCell />
                                                <TableCell />
                                                {!!fields.length && (
                                                    <TableCell style={{ width: 110 }}>
                                                        {t(
                                                            `${PRODUCTS_NS}.default_variety`
                                                        )}{' '}
                                                        {errors.defaultVariety && (
                                                            <FontAwesomeIcon
                                                                className='error-icon'
                                                                name='exclamation-circle'
                                                                title={
                                                                    errors.defaultVariety
                                                                }
                                                            />
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {fields.map((name, idx) => (
                                                <TableRow key={name}>
                                                    <TableCell>
                                                        <FontAwesomeIcon
                                                            className='icon'
                                                            name='remove'
                                                            title={t(
                                                                `${PRODUCTS_NS}.remove_variety`
                                                            )}
                                                            // eslint-disable-next-line react/jsx-no-bind
                                                            onClick={() =>
                                                                fields.remove(idx)
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className='form-field'>
                                                            <Field
                                                                name={`${name}.name`}
                                                                type='text'
                                                                validate={required(
                                                                    t('required_field')
                                                                )}>
                                                                {({ input, meta }) => (
                                                                    <div className='field-input'>
                                                                        <input
                                                                            {...input}
                                                                            placeholder={t(
                                                                                `${PRODUCTS_NS}.variety_name`
                                                                            )}
                                                                        />
                                                                        {meta.error &&
                                                                            meta.touched && (
                                                                                <FontAwesomeIcon
                                                                                    className='error-icon'
                                                                                    name='exclamation-circle'
                                                                                    title={
                                                                                        meta.error
                                                                                    }
                                                                                />
                                                                            )}
                                                                    </div>
                                                                )}
                                                            </Field>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className='form-field'>
                                                            <Field
                                                                name={`${name}.price`}
                                                                type='number'
                                                                // eslint-disable-next-line react/jsx-no-bind
                                                                validate={v =>
                                                                    getDefaultVarietiesNumber(
                                                                        v
                                                                    )
                                                                        ? required(
                                                                              t(
                                                                                  'required_field'
                                                                              )
                                                                          )
                                                                        : undefined
                                                                }>
                                                                {({ input, meta }) => (
                                                                    <div className='field-input price'>
                                                                        <input
                                                                            {...input}
                                                                            placeholder={t(
                                                                                `${PRODUCTS_NS}.variety_price`
                                                                            )}
                                                                            min={0}
                                                                            step='any'
                                                                        />
                                                                        {meta.error &&
                                                                            meta.touched && (
                                                                                <FontAwesomeIcon
                                                                                    className='error-icon'
                                                                                    name='exclamation-circle'
                                                                                    title={
                                                                                        meta.error
                                                                                    }
                                                                                />
                                                                            )}
                                                                    </div>
                                                                )}
                                                            </Field>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell
                                                        style={{ textAlign: 'center' }}>
                                                        <Field
                                                            name={`${name}.isDefault`}
                                                            type='checkbox'
                                                            component='input'
                                                            defaultValue={false}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </FieldArray>
                        </>
                    )}
                    <div className='action-buttons'>
                        <button className='save' type='submit' disabled={disabled}>
                            {t('save')}
                        </button>
                        <DeleteButton
                            disabled={disabled || _.isNil(values.id)}
                            title={t(`${PRODUCTS_NS}.delete_product`)}
                            message={t(`${PRODUCTS_NS}.delete_product_confirm`, values)}
                            onAccept={handleProductDeletion(values.id, form)}
                        />
                        <div style={{ alignSelf: 'center' }}>
                            {loading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                    </div>
                </form>
            );
        },
        [
            handleHasVarietiesChange,
            handleProductDeletion,
            hasVarieties,
            loading,
            productCategories,
            t
        ]
    );

    const handleFormSubmit = React.useCallback(
        (values: Partial<Product>, form: FormApi) => {
            setLoading(true);
            const updatedValues: Partial<Product> = {
                ...values
            };

            if (_.has(values, 'productVarieties')) {
                updatedValues.productVarieties = values.productVarieties.map(p => ({
                    ...p,
                    price: !_.isNil(p.price) ? Number(p.price) : p.price
                }));
            }

            dispatch(saveUpdateProduct(updatedValues))
                .then(() => {
                    resetStateAndForm(null)();
                    form.reset({});

                    ['name', 'description', 'productCategoryID', 'price'].forEach(
                        field => {
                            form.resetFieldState(field);
                        }
                    );
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, resetStateAndForm]
    );

    const groupedProducts = _.groupBy(
        products,
        ({ productCategoryID }) => productCategoryID
    );

    return (
        <div className='admin-form main-content products'>
            <Sidebar>
                <div className='content'>
                    {productCategories.map(({ name, id }) => (
                        <div className='category-products' key={id}>
                            <div className='category'>
                                <span className='name'>{name}</span>
                                <FontAwesomeIcon
                                    name='plus'
                                    className='new-product'
                                    onClick={resetStateAndForm(id)}
                                />
                            </div>
                            <div className='products'>
                                {(groupedProducts[id] || ([] as Product[])).map(p => (
                                    <div
                                        key={p.id}
                                        className={classnames('product item', {
                                            selected:
                                                p.id === _.get(selectedProduct, 'id')
                                        })}
                                        onClick={setProduct(p)}>
                                        {p.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Sidebar>
            <div className='products-form'>
                <Form
                    initialValues={{ ...selectedProduct }}
                    render={renderForm}
                    onSubmit={handleFormSubmit}
                    validate={validateForm}
                    mutators={{ ...arrayMutators }}
                />
            </div>
        </div>
    );
};

export default Products;
